// src/pages/BlogPostDetail.jsx
import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { blogPosts } from '../data/blogData';
import doctorImage from "../Assets/TeamandOthers/DR1.png";

const BlogPostDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const post = blogPosts.find((p) => p.id === parseInt(id));

  // Retrieve state passed from the Blog list
  const returnPage = location.state?.fromPage || 1;
  const returnCat = location.state?.fromCat || "All";

  // Scroll to top whenever the ID changes (for related posts)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleShare = async () => {
    const shareUrl = window.location.href;

    // 1. Try modern Clipboard API (Works on HTTPS/Localhost)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copied to clipboard!");
        return;
      } catch (err) {
        console.error("Clipboard API failed, trying fallback...", err);
      }
    }

    // 2. Fallback for Windows/Insecure Contexts (HTTP/IP Address)
    try {
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        alert("Link copied to clipboard!");
      } else {
        throw new Error('Copy command failed');
      }
    } catch (err) {
      alert("Please copy the URL manually from the address bar.");
    }
  };

  if (!post) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-2xl font-bold text-gray-800">Post not found.</h2>
        <Link to="/blog" className="text-blue-600 underline mt-4 inline-block">Return to Blog</Link>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 pb-20">
      {/* Editorial Header */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Back Link carrying page, category, and specific post ID for scroll */}
            <Link 
              to="/blog" 
              state={{ returnPage, returnCat, returnPostId: post.id }}
              className="inline-flex items-center gap-2 text-white hover:text-white transition-colors mb-8 group"
            >
              <span className="transform group-hover:-translate-x-1 transition-transform">←</span> 
              Back to Health Library
            </Link>
            
            <span className="block text-blue-300 font-bold uppercase tracking-widest text-sm mb-4">
              {post.category} • {post.date}
            </span>
            
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-8">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 border-t border-white/10 pt-8">
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-blue-400 bg-white shadow-lg">
                <img src={doctorImage} alt="Dr Sridhar Reddy" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-bold text-lg leading-none">Dr. Arumalla Sridhar Reddy</p>
                <p className="text-blue-300 text-sm mt-1">BDS, MDS • {post.readTime}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <section className="-mt-12 relative z-10">
        <div className="container mx-auto px-6 max-w-4xl">
          <article className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100">
            <div className="w-full h-[300px] md:h-[500px] overflow-hidden">
              <img 
                src={post.image} 
                alt={post.title} 
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-8 md:p-16">
              <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100">
                <div 
                  className="prose prose-base md:prose-lg max-w-none 
                             prose-headings:text-blue-900 prose-headings:font-bold 
                             prose-p:text-gray-700 prose-li:text-gray-700 
                             prose-h4:text-2xl prose-h4:mt-8 prose-h4:mb-4
                             marker:text-black"
                  dangerouslySetInnerHTML={{ __html: post.content }} 
                />
              </div>

              {/* Author Footer Card */}
              <div className="mt-16 p-8 bg-blue-50 rounded-2xl border border-blue-100 flex flex-col md:flex-row items-center gap-6">
                <img src={doctorImage} className="w-20 h-20 rounded-full border-4 border-white shadow-md" alt="Author" />
                <div className="text-center md:text-left flex-grow">
                  <h5 className="text-xl font-bold text-gray-900">Dr. Arumalla Sridhar Reddy</h5>
                  <p className="text-gray-600 text-sm md:text-base italic mt-1">
                    "Oral health is a window to your overall health. Our goal is to provide world-class dental care with a personalized touch."
                  </p>
                </div>
                <button 
                  onClick={handleShare}
                  className="whitespace-nowrap px-6 py-3 bg-white text-blue-700 font-bold rounded-xl border border-blue-200 hover:bg-blue-600 hover:text-white transition-all shadow-sm flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="4" cy="12" r="3"/>
                    <circle cx="20" cy="5" r="3"/>
                    <circle cx="20" cy="19" r="3"/>
                    <line x1="8.5" y1="9.5" x2="15.5" y2="5.5"/>
                    <line x1="8.5" y1="14.5" x2="15.5" y2="18.5"/>
                  </svg>
                  Share this Article
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles Section */}
          <div className="mt-20">
            <div className="flex items-center justify-between mb-10">
              <h3 className="text-3xl font-bold text-gray-900">More Dental Guides</h3>
              <Link to="/blog" state={{ returnPage, returnCat, returnPostId: post.id }} className="text-blue-600 font-bold hover:underline">View All Articles →</Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts
                .filter(p => p.id !== post.id)
                .sort(() => 0.5 - Math.random())
                .slice(0, 3)
                .map(p => (
                <Link 
                  key={p.id} 
                  to={`/blog/${p.id}`} 
                  state={{ fromPage: returnPage, fromCat: returnCat }}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="h-40 overflow-hidden">
                    <img src={p.image} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" alt={p.title} />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-tighter mb-2 block">{p.category}</span>
                    <h4 className="font-bold text-gray-900 line-clamp-2 group-hover:text-blue-700 transition-colors">
                      {p.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPostDetail;